import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import {
  Search,
  X,
  Plus,
  Save,
  GripVertical,
  LayoutDashboard,
  Trash2,
  Package,
  Users,
  Pencil,
} from "lucide-react";
import { icons as lucideIcons } from "lucide-react";
import { ReactGridLayout } from "react-grid-layout";
import "react-grid-layout/css/styles.css";

import {
  getHomeViews,
  getHomeLayout,
  saveHomeLayout,
  createHomeView,
  updateHomeView,
} from "../../../api/home";
import { useRightSidebar } from "../../contexts/RightSidebarContext";
import { usePopup } from "../../contexts/PopupContext";
import CustomQueryWidget from "./Widgets/CustomQueryWidget";
import WidgetBuilderModal from "./Widgets/WidgetBuilderModal";
import OverviewView from "./OverviewView";

/* ─── Dark mode ─── */
const useDark = () => {
  const [dark, setDark] = useState(
    () =>
      typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark"),
  );
  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() =>
      setDark(el.classList.contains("dark")),
    );
    obs.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return dark;
};

/* ─── Dynamic icon lookup ─── */
const pascalToKebab = (str) =>
  str.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
const LUCIDE_ICON_ENTRIES = Object.entries(lucideIcons)
  .filter(([name]) => !/^\d/.test(name) && name.length > 1)
  .map(([name, component]) => ({ key: pascalToKebab(name), name, component }))
  .sort((a, b) => a.key.localeCompare(b.key));
const LUCIDE_ICON_MAP = {};
LUCIDE_ICON_ENTRIES.forEach((e) => {
  LUCIDE_ICON_MAP[e.key] = e.component;
});
const homeViewIcon = (key) =>
  LUCIDE_ICON_MAP[key] || lucideIcons.LayoutDashboard;

const POPULAR_ICON_KEYS = new Set([
  "layout-dashboard",
  "home",
  "globe",
  "earth",
  "radar",
  "shield",
  "shield-alert",
  "shield-check",
  "sliders-horizontal",
  "crosshair",
  "newspaper",
  "file-text",
  "bookmark",
  "rss",
  "search",
  "star",
  "heart",
  "bell",
  "eye",
  "lock",
  "unlock",
  "zap",
  "flame",
  "target",
  "flag",
  "tag",
  "hash",
  "at-sign",
  "activity",
  "trending-up",
  "bar-chart-3",
  "pie-chart",
  "cpu",
  "server",
  "database",
  "hard-drive",
  "wifi",
  "cloud",
  "folder",
  "file",
  "code",
  "terminal",
  "bug",
  "skull",
  "alert-triangle",
  "alert-circle",
  "info",
  "check-circle",
  "user",
  "users",
  "building",
  "building-2",
  "briefcase",
  "calendar",
  "clock",
  "map-pin",
  "compass",
  "navigation",
  "send",
  "mail",
  "link",
  "external-link",
  "download",
  "upload",
  "share-2",
  "layers",
  "grid",
  "list",
  "layout",
  "monitor",
  "smartphone",
  "key",
  "fingerprint",
  "scan",
]);

/* ─── HSL helpers ─── */
const hslToRgb = (h, s, l) => {
  h /= 360;
  s /= 100;
  l /= 100;
  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
};
const rgbToHsl = (r, g, b) => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h = 0,
    s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
};
const parseRgba = (rgba) => {
  const m = rgba?.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  return m ? { r: +m[1], g: +m[2], b: +m[3] } : { r: 14, g: 165, b: 233 };
};
const toRgba = (r, g, b) => `rgba(${r},${g},${b},0.85)`;

/* ─── ColorWheelPicker ─── */
// Kept with inline styles — canvas rendering requires imperative color values
const ColorWheelPicker = ({ value, onChange, dark }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [hsl, setHsl] = useState(() => {
    const { r, g, b } = parseRgba(value);
    return rgbToHsl(r, g, b);
  });
  const [dragging, setDragging] = useState(null);
  const size = 180,
    ringWidth = 22,
    discRadius = size / 2 - ringWidth - 8,
    cx = size / 2,
    cy = size / 2;

  useEffect(() => {
    const { r, g, b } = parseRgba(value);
    setHsl(rgbToHsl(r, g, b));
  }, [value]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, size, size);
    for (let a = 0; a < 360; a += 0.5) {
      ctx.beginPath();
      ctx.arc(
        cx,
        cy,
        size / 2 - ringWidth / 2,
        ((a - 1) * Math.PI) / 180,
        ((a + 1) * Math.PI) / 180,
      );
      ctx.lineWidth = ringWidth;
      const { r, g, b } = hslToRgb(a, 100, 50);
      ctx.strokeStyle = `rgb(${r},${g},${b})`;
      ctx.stroke();
    }
    const imgData = ctx.createImageData(discRadius * 2, discRadius * 2);
    for (let py = 0; py < discRadius * 2; py++)
      for (let px = 0; px < discRadius * 2; px++) {
        const dx = px - discRadius,
          dy = py - discRadius;
        if (Math.sqrt(dx * dx + dy * dy) <= discRadius) {
          const s2 = ((dx / discRadius + 1) / 2) * 100,
            l2 = (1 - (dy / discRadius + 1) / 2) * 100;
          const { r, g, b } = hslToRgb(hsl.h, s2, l2);
          const i = (py * discRadius * 2 + px) * 4;
          imgData.data[i] = r;
          imgData.data[i + 1] = g;
          imgData.data[i + 2] = b;
          imgData.data[i + 3] = 255;
        }
      }
    const off = document.createElement("canvas");
    off.width = discRadius * 2;
    off.height = discRadius * 2;
    off.getContext("2d").putImageData(imgData, 0, 0);
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, discRadius, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(off, cx - discRadius, cy - discRadius);
    ctx.restore();
    ctx.beginPath();
    ctx.arc(cx, cy, discRadius + 1, 0, Math.PI * 2);
    ctx.lineWidth = 3;
    ctx.strokeStyle = dark ? "rgba(0,0,0,0.7)" : "rgba(0,0,0,0.2)";
    ctx.stroke();
    const ha2 = (hsl.h * Math.PI) / 180,
      hR = size / 2 - ringWidth / 2;
    ctx.beginPath();
    ctx.arc(
      cx + hR * Math.cos(ha2),
      cy + hR * Math.sin(ha2),
      ringWidth / 2 - 2,
      0,
      Math.PI * 2,
    );
    ctx.lineWidth = 2;
    ctx.strokeStyle = "white";
    ctx.stroke();
    const hr = hslToRgb(hsl.h, 100, 50);
    ctx.fillStyle = `rgb(${hr.r},${hr.g},${hr.b})`;
    ctx.fill();
    const slX = cx + ((hsl.s / 100) * 2 - 1) * discRadius,
      slY = cy + (1 - (hsl.l / 100) * 2) * discRadius;
    ctx.beginPath();
    ctx.arc(slX, slY, 6, 0, Math.PI * 2);
    ctx.lineWidth = 2;
    ctx.strokeStyle = "white";
    ctx.stroke();
    const sr = hslToRgb(hsl.h, hsl.s, hsl.l);
    ctx.fillStyle = `rgb(${sr.r},${sr.g},${sr.b})`;
    ctx.fill();
  }, [hsl, dark, size, ringWidth, discRadius, cx, cy]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: (e.touches ? e.touches[0].clientX : e.clientX) - rect.left,
      y: (e.touches ? e.touches[0].clientY : e.clientY) - rect.top,
    };
  };
  const updateHue = (x, y) => {
    const deg = ((Math.atan2(y - cy, x - cx) * 180) / Math.PI + 360) % 360;
    const n = { ...hsl, h: Math.round(deg) };
    setHsl(n);
    const { r, g, b } = hslToRgb(n.h, n.s, n.l);
    onChange(toRgba(r, g, b));
  };
  const updateSL = (x, y) => {
    let dx = (x - cx) / discRadius,
      dy = (y - cy) / discRadius;
    const d = Math.sqrt(dx * dx + dy * dy);
    if (d > 1) {
      dx /= d;
      dy /= d;
    }
    const n = {
      ...hsl,
      s: Math.round(((dx + 1) / 2) * 100),
      l: Math.round((1 - (dy + 1) / 2) * 100),
    };
    setHsl(n);
    const { r, g, b } = hslToRgb(n.h, n.s, n.l);
    onChange(toRgba(r, g, b));
  };
  const handleStart = (e) => {
    e.preventDefault();
    const { x, y } = getPos(e);
    const dx = x - cx,
      dy = y - cy,
      dist = Math.sqrt(dx * dx + dy * dy);
    if (dist >= size / 2 - ringWidth && dist <= size / 2) {
      setDragging("ring");
      updateHue(x, y);
    } else if (dist <= discRadius) {
      setDragging("disc");
      updateSL(x, y);
    }
  };
  const handleMove = useCallback(
    (e) => {
      if (!dragging) return;
      e.preventDefault();
      const rect = canvasRef.current.getBoundingClientRect();
      const cX = e.touches ? e.touches[0].clientX : e.clientX;
      const cY = e.touches ? e.touches[0].clientY : e.clientY;
      if (dragging === "ring") updateHue(cX - rect.left, cY - rect.top);
      else updateSL(cX - rect.left, cY - rect.top);
    },
    [dragging],
  );
  const handleEnd = useCallback(() => {
    if (dragging) setDragging(null);
  }, [dragging]);

  useEffect(() => {
    if (!dragging) return;
    const o = { passive: false };
    window.addEventListener("mousemove", handleMove, o);
    window.addEventListener("mouseup", handleEnd);
    window.addEventListener("touchmove", handleMove, o);
    window.addEventListener("touchend", handleEnd);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleEnd);
    };
  }, [dragging, handleMove, handleEnd]);

  return (
    <div ref={containerRef} className="flex flex-col items-center gap-2">
      <canvas
        ref={canvasRef}
        width={size}
        height={size}
        style={{
          width: size,
          height: size,
          cursor: dragging ? "grabbing" : "crosshair",
          touchAction: "none",
        }}
        onMouseDown={handleStart}
        onTouchStart={handleStart}
      />
      <div className="flex items-center gap-2">
        <div
          className="h-5 w-5 rounded-full border border-white/20"
          style={{ background: value }}
        />
        <span className="font-mono text-[10px] text-mutedText">{value}</span>
      </div>
    </div>
  );
};

/* ─── Theme tokens ─── */
// Kept for components that use imperative inline styles (grid library, canvas, react-grid-layout)
const getTheme = (dark) =>
  dark
    ? {
        barBg: "rgba(11,15,20,0.95)",
        barBorder: "rgba(14,165,233,0.15)",
        barShadow: "0 8px 32px rgba(0,0,0,0.4), 0 0 1px rgba(14,165,233,0.2)",
        cardBg: "rgba(15,23,42,0.60)",
        cardBorder: "rgba(14,165,233,0.12)",
        cardBorderHover: "rgba(14,165,233,0.25)",
        inputBg: "rgba(17,24,39,0.60)",
        inputBorder: "rgba(14,165,233,0.12)",
        inputText: "rgba(226,232,240,0.9)",
        textPrimary: "rgba(226,232,240,0.9)",
        textSecondary: "rgba(148,163,184,0.7)",
        textMuted: "rgba(148,163,184,0.4)",
        activeBg: "rgba(14,165,233,0.15)",
        activeBorder: "rgba(14,165,233,0.30)",
        activeText: "rgba(14,165,233,0.9)",
        dangerText: "rgba(248,113,113,0.9)",
      }
    : {
        barBg: "rgba(255,255,255,0.92)",
        barBorder: "rgba(203,213,225,0.5)",
        barShadow: "0 4px 24px rgba(0,0,0,0.08), 0 0 1px rgba(0,0,0,0.05)",
        cardBg: "rgba(241,245,249,0.8)",
        cardBorder: "rgba(203,213,225,0.5)",
        cardBorderHover: "rgba(14,165,233,0.25)",
        inputBg: "rgba(241,245,249,0.8)",
        inputBorder: "rgba(203,213,225,0.5)",
        inputText: "rgba(15,23,42,0.9)",
        textPrimary: "rgba(15,23,42,0.9)",
        textSecondary: "rgba(71,85,105,0.8)",
        textMuted: "rgba(148,163,184,0.6)",
        activeBg: "rgba(14,165,233,0.10)",
        activeBorder: "rgba(14,165,233,0.30)",
        activeText: "rgba(2,132,199,1)",
        dangerText: "rgba(220,38,38,0.9)",
      };

/* ─── Grid constants ─── */
const GRID_COLS = 12;
const GRID_MARGIN = [12, 12];
const NORMAL_ROW_HEIGHT = 64;

/* ─── Blueprint GridBackground ─── */
const GridBackground = ({ width, rowHeight, cols, margin }) => {
  const colWidth = (width - margin[0] * (cols + 1)) / cols;
  const rowCount = 40;
  const totalHeight = rowCount * (rowHeight + margin[1]) + margin[1];
  return (
    <svg
      className="pointer-events-none absolute inset-0"
      width={width}
      height={totalHeight}
      style={{ zIndex: 0 }}
    >
      {Array.from({ length: cols + 1 }, (_, i) => {
        const x = margin[0] + i * (colWidth + margin[0]);
        return (
          <line
            key={`col-${i}`}
            x1={x}
            y1={0}
            x2={x}
            y2={totalHeight}
            stroke="rgba(14,165,233,0.08)"
            strokeWidth={1}
            strokeDasharray="4 4"
          />
        );
      })}
      {Array.from({ length: rowCount + 1 }, (_, i) => {
        const y = margin[1] + i * (rowHeight + margin[1]);
        return (
          <line
            key={`row-${i}`}
            x1={0}
            y1={y}
            x2={width}
            y2={y}
            stroke="rgba(14,165,233,0.06)"
            strokeWidth={1}
            strokeDasharray="4 4"
          />
        );
      })}
      {Array.from({ length: cols }, (_, i) => {
        const x = margin[0] + i * (colWidth + margin[0]);
        return (
          <rect
            key={`col-fill-${i}`}
            x={x}
            y={0}
            width={colWidth}
            height={totalHeight}
            fill="rgba(14,165,233,0.02)"
          />
        );
      })}
    </svg>
  );
};

/* ─── Widget type → component map ─── */
const WIDGET_COMPONENTS = { custom_query: CustomQueryWidget };

/* ─── Widget Shell ─── */
const WidgetShell = ({ widget, t, dark }) => {
  const Icon = homeViewIcon(widget.config?.icon || "square");
  const WidgetComponent = WIDGET_COMPONENTS[widget.widget_type];
  return (
    <div
      className="flex h-full w-full flex-col overflow-hidden rounded-lg"
      style={{ background: t.cardBg, border: `1px solid ${t.cardBorder}` }}
    >
      <div
        className="flex shrink-0 items-center justify-between px-3 py-2"
        style={{ borderBottom: `1px solid ${t.cardBorder}`, minHeight: 36 }}
      >
        <div className="flex min-w-0 items-center gap-2">
          <Icon
            className="h-3.5 w-3.5 shrink-0"
            style={{ color: t.textSecondary }}
            strokeWidth={2}
            aria-hidden
          />
          <span
            className="truncate text-xs font-medium"
            style={{ color: t.textPrimary }}
          >
            {widget.config?.label || widget.widget_type}
          </span>
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        {WidgetComponent ? (
          <WidgetComponent widget={widget} t={t} dark={dark} />
        ) : (
          <div className="flex h-full items-center justify-center p-4">
            <span className="text-[11px]" style={{ color: t.textMuted }}>
              {widget.widget_type.replace(/_/g, " ")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── EditTile ─── */
const EditTile = ({ widget, onRemove, dark }) => {
  const Icon = homeViewIcon(widget.config?.icon || "square");
  return (
    <div
      className="flex h-full w-full select-none items-center gap-2 overflow-hidden rounded-lg px-2"
      style={{
        background: dark ? "rgba(14,165,233,0.07)" : "rgba(14,165,233,0.06)",
        border: `1px solid ${dark ? "rgba(14,165,233,0.20)" : "rgba(14,165,233,0.18)"}`,
        boxShadow: dark
          ? "0 1px 4px rgba(0,0,0,0.3), inset 0 1px 0 rgba(14,165,233,0.08)"
          : "0 1px 4px rgba(0,0,0,0.06), inset 0 1px 0 rgba(14,165,233,0.06)",
      }}
    >
      <GripVertical
        className="drag-handle h-3.5 w-3.5 shrink-0 cursor-grab active:cursor-grabbing"
        style={{
          color: dark ? "rgba(14,165,233,0.35)" : "rgba(14,165,233,0.4)",
        }}
        strokeWidth={2}
        aria-hidden
      />
      <Icon
        className="h-3.5 w-3.5 shrink-0"
        style={{ color: dark ? "rgba(14,165,233,0.6)" : "rgba(2,132,199,0.7)" }}
        strokeWidth={2}
        aria-hidden
      />
      <span
        className="truncate text-[11px] font-medium"
        style={{
          color: dark ? "rgba(226,232,240,0.75)" : "rgba(15,23,42,0.7)",
        }}
      >
        {widget.config?.label || widget.widget_type}
      </span>
      <div className="flex-1" />
      <span
        className="shrink-0 rounded px-1.5 py-0.5 font-mono text-[9px]"
        style={{
          color: dark ? "rgba(14,165,233,0.45)" : "rgba(2,132,199,0.5)",
          background: dark ? "rgba(14,165,233,0.06)" : "rgba(14,165,233,0.05)",
          border: `1px solid ${dark ? "rgba(14,165,233,0.10)" : "rgba(14,165,233,0.10)"}`,
        }}
      >
        {widget.w}×{widget.h}
      </span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove(widget.widget_id);
        }}
        className="shrink-0 rounded p-1 transition hover:bg-errorText/15 cursor-pointer"
        title="Remove widget"
      >
        <Trash2
          className="h-3 w-3"
          style={{
            color: dark ? "rgba(248,113,113,0.6)" : "rgba(220,38,38,0.5)",
          }}
          strokeWidth={2}
          aria-hidden
        />
      </button>
    </div>
  );
};

/* ─── Widget Catalog Panel ─── */
const WIDGET_TABS = [
  { key: "my", label: "My Widgets", icon: LayoutDashboard },
  { key: "included", label: "Included", icon: Package },
  { key: "shared", label: "Shared with Me", icon: Users },
];

const WidgetCatalogPanel = ({
  onOpenBuilder,
  widgets,
  onEditWidget,
  onRemoveWidget,
  t,
  dark,
}) => {
  const [activeTab, setActiveTab] = useState("my");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  return (
    <div className="flex h-full flex-col font-poppins font-normal">
      {/* Build new widget button */}
      <div className="shrink-0 border-b border-mainDivider px-3 pb-2 pt-3">
        <button
          onClick={onOpenBuilder}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2"
          style={{
            background: t.activeBg,
            border: `1px solid ${t.activeBorder}`,
          }}
        >
          <div
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
            style={{
              background: dark
                ? "rgba(14,165,233,0.2)"
                : "rgba(14,165,233,0.15)",
              border: `1px solid ${t.activeBorder}`,
            }}
          >
            <Plus
              className="h-3.5 w-3.5"
              style={{ color: t.activeText }}
              strokeWidth={2.5}
              aria-hidden
            />
          </div>
          <div className="min-w-0">
            <div
              className="text-xs font-medium"
              style={{ color: t.activeText }}
            >
              Build New Widget
            </div>
            <div className="text-[10px]" style={{ color: t.textMuted }}>
              Query data & choose visualization
            </div>
          </div>
        </button>
      </div>

      {/* Tab selector */}
      <div className="flex shrink-0 items-center gap-0.5 border-b border-mainDivider px-3 py-2">
        {WIDGET_TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-[10px] transition cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2"
              style={{
                background: isActive ? t.activeBg : "transparent",
                color: isActive ? t.activeText : t.textMuted,
                border: isActive
                  ? `1px solid ${t.activeBorder}`
                  : "1px solid transparent",
              }}
            >
              <Icon size={10} strokeWidth={2} aria-hidden />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div
        className="flex-1 overflow-y-auto p-3"
        style={{ scrollbarWidth: "thin" }}
      >
        {/* My Widgets */}
        {activeTab === "my" && (
          <div className="space-y-1">
            {widgets.length === 0 ? (
              <div className="py-8 text-center text-[11px] text-mutedText">
                <LayoutDashboard
                  size={24}
                  className="mx-auto mb-2 opacity-30"
                  aria-hidden
                />
                No widgets on this view yet.
                <br />
                Click "Build New Widget" above to get started.
              </div>
            ) : (
              widgets.map((w) => (
                <div
                  key={w.widget_id}
                  className="group overflow-hidden rounded-lg transition"
                  style={{
                    border: `1px solid ${
                      confirmDeleteId === w.widget_id
                        ? dark
                          ? "rgba(248,113,113,0.3)"
                          : "rgba(220,38,38,0.3)"
                        : t.cardBorder
                    }`,
                  }}
                >
                  {confirmDeleteId === w.widget_id ? (
                    <div className="px-3 py-2.5">
                      <div className="mb-2 text-[11px] text-mainText">
                        Remove{" "}
                        <strong>{w.config?.label || "this widget"}</strong> from
                        dashboard?
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            onRemoveWidget(w.widget_id);
                            setConfirmDeleteId(null);
                          }}
                          className="flex-1 rounded-lg px-2 py-1 text-[10px] text-white transition cursor-pointer"
                          style={{ background: "rgba(239,68,68,0.8)" }}
                        >
                          Remove
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          className="flex-1 rounded-lg border border-surfaceBorder px-2 py-1 text-[10px] text-mutedText transition hover:text-mainText cursor-pointer"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[11px] font-medium text-mainText">
                          {w.config?.label || w.widget_type}
                        </div>
                        {w.config?.collection && (
                          <div className="truncate text-[9px] text-mutedText">
                            {w.config.collection}
                            {w.config.vizType ? ` · ${w.config.vizType}` : ""}
                          </div>
                        )}
                      </div>
                      {w.widget_type === "custom_query" && (
                        <button
                          onClick={() => onEditWidget(w)}
                          className="rounded-lg p-1 opacity-0 transition group-hover:opacity-100 cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2"
                          title="Edit widget"
                        >
                          <Pencil
                            size={11}
                            style={{ color: t.activeText }}
                            strokeWidth={2}
                            aria-hidden
                          />
                        </button>
                      )}
                      <button
                        onClick={() => setConfirmDeleteId(w.widget_id)}
                        className="rounded-lg p-1 opacity-0 transition hover:bg-errorText/15 group-hover:opacity-100 cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2"
                        title="Remove from dashboard"
                      >
                        <Trash2
                          size={11}
                          style={{
                            color: dark
                              ? "rgba(248,113,113,0.6)"
                              : "rgba(220,38,38,0.5)",
                          }}
                          aria-hidden
                        />
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Included */}
        {activeTab === "included" && (
          <div className="py-8 text-center text-[11px] text-mutedText">
            <Package
              size={24}
              className="mx-auto mb-2 opacity-30"
              aria-hidden
            />
            No included widgets available yet.
            <br />
            <span className="text-[10px]">
              Pre-built widgets will appear here.
            </span>
          </div>
        )}

        {/* Shared */}
        {activeTab === "shared" && (
          <div className="py-8 text-center text-[11px] text-mutedText">
            <Users size={24} className="mx-auto mb-2 opacity-30" aria-hidden />
            No shared widgets yet.
            <br />
            <span className="text-[10px]">
              Widgets shared by other users will appear here.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   MainHome
   ═══════════════════════════════════════════ */
const MainHome = () => {
  const dark = useDark();
  const t = useMemo(() => getTheme(dark), [dark]);
  const { setIsRightOpen, setRightComponent, setRightSidebarMode } =
    useRightSidebar();
  const { openPopUp, closePopUp } = usePopup();

  const gridContainerRef = useRef(null);
  const [lockedWidth, setLockedWidth] = useState(0);
  const [liveWidth, setLiveWidth] = useState(0);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const calcWidth = () => {
      const el = gridContainerRef.current;
      if (el) setLockedWidth(Math.floor(el.getBoundingClientRect().width));
    };
    calcWidth();
    window.addEventListener("resize", calcWidth);
    return () => window.removeEventListener("resize", calcWidth);
  }, []);

  useEffect(() => {
    if (!isEditing) return;
    const el = gridContainerRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      for (const entry of entries)
        setLiveWidth(Math.floor(entry.contentRect.width));
    });
    obs.observe(el);
    setLiveWidth(Math.floor(el.getBoundingClientRect().width));
    return () => obs.disconnect();
  }, [isEditing]);

  const containerWidth = isEditing ? liveWidth : lockedWidth;

  const [views, setViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [savedWidgets, setSavedWidgets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [showCreateViewModal, setShowCreateViewModal] = useState(false);
  const [editingView, setEditingView] = useState(null);
  const [createViewForm, setCreateViewForm] = useState({
    name: "",
    icon: "layout-dashboard",
    color: "rgba(14,165,233,0.85)",
    allow_scroll: false,
  });
  const [modalIconSearch, setModalIconSearch] = useState("");
  const [editingWidgetData, setEditingWidgetData] = useState(null);

  const activeView = useMemo(
    () => views.find((v) => v.document_id === activeViewId),
    [views, activeViewId],
  );
  const allowScroll = activeView?.allow_scroll ?? false;
  const isFixed = activeView?.is_fixed ?? false;

  const gridLayout = useMemo(
    () =>
      widgets.map((w) => ({
        i: w.widget_id,
        x: w.x,
        y: w.y,
        w: w.w,
        h: w.h,
        minW: 2,
        minH: 2,
        static: !isEditing,
      })),
    [widgets, isEditing],
  );

  const syncLayout = useCallback((newLayout) => {
    setWidgets((prev) =>
      prev.map((w) => {
        const item = newLayout.find((l) => l.i === w.widget_id);
        return item ? { ...w, x: item.x, y: item.y, w: item.w, h: item.h } : w;
      }),
    );
  }, []);

  const loadViews = useCallback(async () => {
    try {
      const res = await getHomeViews();
      const fetched = res?.data?.views || [];
      setViews(fetched);
      if (!activeViewId && fetched.length > 0) {
        const def = fetched.find((v) => v.is_default);
        setActiveViewId(def ? def.document_id : fetched[0].document_id);
      }
    } catch (e) {
      console.error("Error loading home views:", e);
    }
  }, [activeViewId]);

  useEffect(() => {
    loadViews();
  }, [loadViews]);

  useEffect(() => {
    if (!activeViewId) return;
    setIsLoading(true);
    getHomeLayout(activeViewId)
      .then((res) => setWidgets(res?.data?.layout?.widgets || []))
      .catch((e) => console.error("Error loading layout:", e))
      .finally(() => setIsLoading(false));
  }, [activeViewId]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.viewId) {
        setActiveViewId(e.detail.viewId);
        setIsEditing(false);
      }
    };
    window.addEventListener("home:switch-view", handler);
    return () => window.removeEventListener("home:switch-view", handler);
  }, []);

  useEffect(() => {
    const handler = () => loadViews();
    window.addEventListener("sidebar:refresh-home-views", handler);
    return () =>
      window.removeEventListener("sidebar:refresh-home-views", handler);
  }, [loadViews]);

  useEffect(() => {
    const handler = () => {
      setEditingView(null);
      setCreateViewForm({
        name: "",
        icon: "layout-dashboard",
        color: "rgba(14,165,233,0.85)",
        allow_scroll: false,
      });
      setModalIconSearch("");
      setShowCreateViewModal(true);
    };
    window.addEventListener("sidebar:home-open-create-modal", handler);
    return () =>
      window.removeEventListener("sidebar:home-open-create-modal", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const view = e.detail?.view;
      if (!view) return;
      setEditingView(view);
      setCreateViewForm({
        name: view.name || "",
        icon: view.icon || "layout-dashboard",
        color: view.color || "rgba(14,165,233,0.85)",
        allow_scroll: view.allow_scroll ?? false,
      });
      setModalIconSearch("");
      setShowCreateViewModal(true);
    };
    window.addEventListener("sidebar:home-open-edit-modal", handler);
    return () =>
      window.removeEventListener("sidebar:home-open-edit-modal", handler);
  }, []);

  const handleModalSubmit = async () => {
    try {
      const payload = {
        name: createViewForm.name,
        icon: createViewForm.icon,
        color: createViewForm.color,
        allow_scroll: createViewForm.allow_scroll,
      };
      let res;
      if (editingView) {
        payload.document_id = editingView.document_id;
        res = await updateHomeView(payload);
      } else {
        res = await createHomeView(payload);
      }
      if (res.data?.status === 200) {
        setShowCreateViewModal(false);
        setEditingView(null);
        loadViews();
        window.dispatchEvent(new CustomEvent("sidebar:refresh-home-views"));
        if (res.data.document_id) setActiveViewId(res.data.document_id);
      } else if (res.data?.status === 409) {
        alert("A home view with that name already exists.");
      }
    } catch (err) {
      console.error(
        editingView ? "Update view failed:" : "Create view failed:",
        err,
      );
    }
  };

  const resetModal = () => {
    setShowCreateViewModal(false);
    setEditingView(null);
    setCreateViewForm({
      name: "",
      icon: "layout-dashboard",
      color: "rgba(14,165,233,0.85)",
      allow_scroll: false,
    });
    setModalIconSearch("");
  };

  const enterEditMode = () => {
    if (isFixed) return;
    setSavedWidgets(JSON.parse(JSON.stringify(widgets)));
    setIsEditing(true);
    setRightComponent(
      <WidgetCatalogPanel
        onOpenBuilder={() => handleOpenBuilderRef.current()}
        widgets={widgets}
        onEditWidget={(w) => handleEditWidgetRef.current(w)}
        onRemoveWidget={(id) => handleRemoveWidget(id)}
        t={t}
        dark={dark}
      />,
    );
    setRightSidebarMode("home_widgets");
    setIsRightOpen(true);
  };
  const cancelEditMode = () => {
    setWidgets(savedWidgets);
    setIsEditing(false);
    setIsRightOpen(false);
  };

  const handleSave = async () => {
    try {
      await saveHomeLayout({ view_id: activeViewId, widgets });
      setIsEditing(false);
      setIsRightOpen(false);
    } catch (e) {
      console.error("Error saving layout:", e);
    }
  };

  useEffect(() => {
    if (isEditing)
      setRightComponent(
        <WidgetCatalogPanel
          onOpenBuilder={() => handleOpenBuilderRef.current()}
          widgets={widgets}
          onEditWidget={(w) => handleEditWidgetRef.current(w)}
          onRemoveWidget={(id) => handleRemoveWidget(id)}
          t={t}
          dark={dark}
        />,
      );
  }, [dark, t, isEditing, widgets]);

  const handleAddWidget = useCallback((widgetDef) => {
    setWidgets((prev) => {
      let maxY = 0;
      for (const w of prev) maxY = Math.max(maxY, w.y + w.h);
      return [
        ...prev,
        {
          widget_id: crypto.randomUUID
            ? crypto.randomUUID()
            : `w-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          widget_type: widgetDef.widget_type,
          x: 0,
          y: maxY,
          w: widgetDef.defaultW || 6,
          h: widgetDef.defaultH || 4,
          config: widgetDef.config || { label: widgetDef.label },
        },
      ];
    });
  }, []);

  const handleSaveBuilderWidget = useCallback(
    (widgetDef) => {
      if (editingWidgetData) {
        setWidgets((prev) =>
          prev.map((w) =>
            w.widget_id === editingWidgetData.widget_id
              ? { ...w, config: widgetDef.config }
              : w,
          ),
        );
        setEditingWidgetData(null);
      } else {
        handleAddWidget({ ...widgetDef, defaultW: 6, defaultH: 4 });
      }
    },
    [editingWidgetData, handleAddWidget],
  );

  const openBuilderModal = useCallback(
    (editWidget = null) => {
      setEditingWidgetData(editWidget);
      openPopUp(
        <WidgetBuilderModal
          dark={dark}
          editingWidget={editWidget}
          onClose={() => {
            closePopUp();
            setEditingWidgetData(null);
          }}
          onSave={(widgetDef) => {
            handleSaveBuilderWidgetRef.current(widgetDef);
            closePopUp();
            setEditingWidgetData(null);
          }}
        />,
      );
    },
    [dark, openPopUp, closePopUp],
  );

  const handleEditWidget = useCallback(
    (widget) => openBuilderModal(widget),
    [openBuilderModal],
  );
  const handleOpenBuilder = useCallback(
    () => openBuilderModal(null),
    [openBuilderModal],
  );

  const handleSaveBuilderWidgetRef = useRef(handleSaveBuilderWidget);
  handleSaveBuilderWidgetRef.current = handleSaveBuilderWidget;
  const handleOpenBuilderRef = useRef(handleOpenBuilder);
  handleOpenBuilderRef.current = handleOpenBuilder;
  const handleEditWidgetRef = useRef(handleEditWidget);
  handleEditWidgetRef.current = handleEditWidget;

  const handleRemoveWidget = useCallback(
    (id) => {
      setWidgets((prev) => {
        const updated = prev.filter((w) => w.widget_id !== id);
        if (activeViewId) {
          saveHomeLayout({ view_id: activeViewId, widgets: updated }).catch(
            (e) => console.error("Failed to save after widget removal:", e),
          );
        }
        return updated;
      });
      setSavedWidgets((prev) => prev.filter((w) => w.widget_id !== id));
    },
    [activeViewId],
  );

  const rowHeight = NORMAL_ROW_HEIGHT;

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden pt-12 font-poppins font-normal text-mainText">
      {/* Resize handle styles */}
      {isEditing && (
        <style>{`
          .react-grid-item > .react-resizable-handle { position: absolute; opacity: 0; transition: opacity 0.15s; z-index: 10; }
          .react-grid-item:hover > .react-resizable-handle { opacity: 1; }
          .react-resizable-handle::after { content: ""; position: absolute; border-radius: 2px; background: rgba(14,165,233,0.5); }
          .react-resizable-handle-se { bottom: 2px; right: 2px; width: 12px; height: 12px; cursor: se-resize; }
          .react-resizable-handle-se::after { bottom: 0; right: 0; width: 8px; height: 8px; border-radius: 0 0 4px 0; }
          .react-resizable-handle-sw { bottom: 2px; left: 2px; width: 12px; height: 12px; cursor: sw-resize; }
          .react-resizable-handle-sw::after { bottom: 0; left: 0; width: 8px; height: 8px; border-radius: 0 0 0 4px; }
          .react-resizable-handle-ne { top: 2px; right: 2px; width: 12px; height: 12px; cursor: ne-resize; }
          .react-resizable-handle-ne::after { top: 0; right: 0; width: 8px; height: 8px; border-radius: 0 4px 0 0; }
          .react-resizable-handle-nw { top: 2px; left: 2px; width: 12px; height: 12px; cursor: nw-resize; }
          .react-resizable-handle-nw::after { top: 0; left: 0; width: 8px; height: 8px; border-radius: 4px 0 0 0; }
          .react-resizable-handle-n { top: 0; left: 50%; transform: translateX(-50%); width: 40px; height: 6px; cursor: n-resize; }
          .react-resizable-handle-n::after { top: 1px; left: 50%; transform: translateX(-50%); width: 24px; height: 3px; }
          .react-resizable-handle-s { bottom: 0; left: 50%; transform: translateX(-50%); width: 40px; height: 6px; cursor: s-resize; }
          .react-resizable-handle-s::after { bottom: 1px; left: 50%; transform: translateX(-50%); width: 24px; height: 3px; }
          .react-resizable-handle-e { right: 0; top: 50%; transform: translateY(-50%); width: 6px; height: 40px; cursor: e-resize; }
          .react-resizable-handle-e::after { right: 1px; top: 50%; transform: translateY(-50%); width: 3px; height: 24px; }
          .react-resizable-handle-w { left: 0; top: 50%; transform: translateY(-50%); width: 6px; height: 40px; cursor: w-resize; }
          .react-resizable-handle-w::after { left: 1px; top: 50%; transform: translateY(-50%); width: 3px; height: 24px; }
        `}</style>
      )}

      {/* Grid area */}
      <div
        ref={gridContainerRef}
        className={`relative flex-1 p-4 ${allowScroll ? "overflow-y-auto" : "overflow-hidden"}`}
        style={{ scrollbarWidth: "thin", transition: "background 0.3s ease" }}
      >
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <span className="text-sm text-mutedText">Loading...</span>
          </div>
        ) : isFixed ? (
          <OverviewView dark={dark} />
        ) : widgets.length === 0 && !isEditing ? (
          <div className="flex h-full flex-col items-center justify-center gap-2">
            <LayoutDashboard
              className="h-10 w-10 text-mutedText"
              strokeWidth={1.5}
              aria-hidden
            />
            <span className="text-sm text-mutedText">No widgets yet</span>
            <span className="text-xs text-mutedText">
              Click the + button to start building
            </span>
          </div>
        ) : containerWidth > 0 ? (
          <div className="relative" style={{ zIndex: 1 }}>
            <ReactGridLayout
              layout={gridLayout}
              cols={GRID_COLS}
              rowHeight={rowHeight}
              width={containerWidth}
              isDraggable={isEditing}
              isResizable={isEditing}
              resizeHandles={["s", "w", "e", "n", "sw", "nw", "se", "ne"]}
              draggableHandle=".drag-handle"
              compactType="vertical"
              onDragStop={(layout) => syncLayout(layout)}
              onResizeStop={(layout) => syncLayout(layout)}
              margin={GRID_MARGIN}
              containerPadding={[0, 0]}
              useCSSTransforms={true}
            >
              {widgets.map((w) => (
                <div key={w.widget_id}>
                  {isEditing ? (
                    <div className="relative h-full w-full overflow-hidden rounded-lg">
                      <div
                        style={{ opacity: 0.4, pointerEvents: "none" }}
                        className="h-full w-full"
                      >
                        <WidgetShell widget={w} t={t} dark={dark} />
                      </div>
                      <div
                        className="absolute inset-0 flex items-center justify-center rounded-lg"
                        style={{
                          background: dark
                            ? "rgba(2,6,14,0.3)"
                            : "rgba(255,255,255,0.2)",
                          border: `1px dashed ${dark ? "rgba(14,165,233,0.25)" : "rgba(14,165,233,0.3)"}`,
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <GripVertical
                            className="drag-handle h-4 w-4 cursor-grab active:cursor-grabbing"
                            style={{
                              color: dark
                                ? "rgba(14,165,233,0.5)"
                                : "rgba(14,165,233,0.6)",
                            }}
                            strokeWidth={2}
                            aria-hidden
                          />
                          <span
                            className="select-none text-[11px] font-medium"
                            style={{
                              color: dark
                                ? "rgba(226,232,240,0.6)"
                                : "rgba(15,23,42,0.5)",
                            }}
                          >
                            {w.config?.label || w.widget_type}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveWidget(w.widget_id);
                            }}
                            className="ml-1 rounded-lg p-1 transition hover:bg-errorText/20 cursor-pointer"
                            title="Remove widget"
                          >
                            <Trash2
                              className="h-3 w-3"
                              style={{
                                color: dark
                                  ? "rgba(248,113,113,0.6)"
                                  : "rgba(220,38,38,0.5)",
                              }}
                              strokeWidth={2}
                              aria-hidden
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <WidgetShell widget={w} t={t} dark={dark} />
                  )}
                </div>
              ))}
            </ReactGridLayout>
          </div>
        ) : null}
      </div>

      {/* Floating action buttons */}
      {!isFixed && (
        <div className="absolute right-5 top-[68px] z-20 flex items-center gap-2">
          {isEditing ? (
            <>
              <button
                onClick={cancelEditMode}
                className="flex items-center gap-1.5 rounded-lg border border-surfaceBorder bg-surface/80 px-3 py-1.5 text-[11px] text-mutedText shadow-login-panel backdrop-blur-md transition hover:text-mainText cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2"
              >
                <X className="h-3 w-3" strokeWidth={2} aria-hidden /> Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-1.5 rounded-lg bg-velBlue px-3 py-1.5 text-[11px] font-normal text-white shadow-login-panel transition hover:opacity-90 cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2"
              >
                <Save className="h-3 w-3" strokeWidth={2} aria-hidden /> Save
              </button>
            </>
          ) : (
            <button
              onClick={enterEditMode}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-surfaceBorder bg-surface/80 shadow-login-panel backdrop-blur-md transition hover:bg-surface cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2"
              title="Edit dashboard"
            >
              <Plus
                className="h-4 w-4 text-velBlue"
                strokeWidth={2}
                aria-hidden
              />
            </button>
          )}
        </div>
      )}

      {/* Create / Edit Home View Modal */}
      {showCreateViewModal &&
        (() => (
          <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-mainBg/60 backdrop-blur-sm"
            onClick={resetModal}
          >
            <div
              className="flex max-h-[85vh] w-[480px] flex-col rounded-lg border border-surfaceBorder bg-popUpbg/90 shadow-login-panel backdrop-blur-lg"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal header */}
              <div className="flex shrink-0 items-center justify-between border-b border-mainDivider px-5 py-4">
                <h3 className="text-sm font-medium text-mainText">
                  {editingView ? "Edit Home View" : "Create Home View"}
                </h3>
                <button
                  className="rounded-lg p-1 text-mutedText transition hover:bg-btnHover hover:text-mainText cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2"
                  onClick={resetModal}
                >
                  <X size={14} aria-hidden />
                </button>
              </div>

              {/* Modal body */}
              <div
                className="flex-1 overflow-y-auto px-5 py-4"
                style={{ scrollbarWidth: "thin" }}
              >
                {/* View name */}
                <div className="mb-3">
                  <label className="mb-1.5 block text-[0.8125rem] font-normal tracking-wide text-mutedText">
                    View Name
                  </label>
                  <input
                    type="text"
                    autoFocus
                    className="w-full rounded-lg border border-inputBorder bg-transparent px-3 py-2.5 text-sm text-mainText outline-none ring-inputFocus/60 transition focus:ring-2 placeholder:text-mutedText"
                    placeholder="e.g. Security Overview"
                    value={createViewForm.name}
                    onChange={(e) =>
                      setCreateViewForm((f) => ({ ...f, name: e.target.value }))
                    }
                  />
                </div>

                {/* Icon + Color */}
                <div className="mb-3 grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1.5 block text-[0.8125rem] font-normal tracking-wide text-mutedText">
                      Icon
                    </label>
                    <div className="relative mb-1">
                      <Search
                        size={11}
                        className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-mutedText"
                        aria-hidden
                      />
                      <input
                        type="text"
                        className="w-full rounded-lg border border-inputBorder bg-transparent py-1.5 pl-7 pr-3 text-xs text-mainText outline-none ring-inputFocus/60 transition focus:ring-2 placeholder:text-mutedText"
                        placeholder="Search icons..."
                        value={modalIconSearch}
                        onChange={(e) => setModalIconSearch(e.target.value)}
                      />
                    </div>
                    <div
                      className="flex max-h-[200px] flex-wrap gap-0.5 overflow-y-auto rounded-lg border border-inputBorder bg-innerBg p-1.5"
                      style={{ scrollbarWidth: "thin" }}
                    >
                      {(() => {
                        const search = modalIconSearch.toLowerCase().trim();
                        let filtered = search
                          ? LUCIDE_ICON_ENTRIES.filter(
                              (e) =>
                                e.key.includes(search) ||
                                e.name.toLowerCase().includes(search),
                            )
                          : LUCIDE_ICON_ENTRIES.filter((e) =>
                              POPULAR_ICON_KEYS.has(e.key),
                            );
                        const ck = createViewForm.icon;
                        if (ck && !filtered.some((e) => e.key === ck)) {
                          const cur = LUCIDE_ICON_ENTRIES.find(
                            (e) => e.key === ck,
                          );
                          if (cur) filtered = [cur, ...filtered];
                        }
                        return filtered.map((entry) => {
                          const IC = entry.component;
                          const isA = createViewForm.icon === entry.key;
                          return (
                            <button
                              key={entry.key}
                              type="button"
                              className="rounded-lg p-1.5 transition cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2"
                              title={entry.name}
                              style={{
                                background: isA
                                  ? "rgba(14,165,233,0.15)"
                                  : "transparent",
                                border: isA
                                  ? "1px solid rgba(14,165,233,0.30)"
                                  : "1px solid transparent",
                              }}
                              onClick={() =>
                                setCreateViewForm((f) => ({
                                  ...f,
                                  icon: entry.key,
                                }))
                              }
                            >
                              <IC
                                size={14}
                                style={{
                                  color: isA ? createViewForm.color : undefined,
                                }}
                                className={isA ? "" : "text-mutedText"}
                                aria-hidden
                              />
                            </button>
                          );
                        });
                      })()}
                    </div>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-[0.8125rem] font-normal tracking-wide text-mutedText">
                      Color
                    </label>
                    <ColorWheelPicker
                      value={createViewForm.color}
                      onChange={(c) =>
                        setCreateViewForm((f) => ({ ...f, color: c }))
                      }
                      dark={dark}
                    />
                  </div>
                </div>

                {/* Allow scrolling toggle */}
                <div className="mb-3">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between rounded-lg border border-inputBorder bg-transparent px-3 py-2.5 transition hover:bg-innerBg cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2"
                    onClick={() =>
                      setCreateViewForm((f) => ({
                        ...f,
                        allow_scroll: !f.allow_scroll,
                      }))
                    }
                  >
                    <div className="flex flex-col items-start">
                      <span className="text-xs font-medium text-mainText">
                        Allow Scrolling
                      </span>
                      <span className="mt-0.5 text-[10px] text-mutedText">
                        {createViewForm.allow_scroll
                          ? "Widgets can extend below the viewport"
                          : "Widgets are locked to the visible area"}
                      </span>
                    </div>
                    {/* Toggle — using design system pattern from §5.16 */}
                    <button
                      role="switch"
                      aria-checked={createViewForm.allow_scroll}
                      className={`relative ml-3 inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border border-inputBorder transition ${
                        createViewForm.allow_scroll
                          ? "bg-velBlue"
                          : "bg-transparent"
                      }`}
                      tabIndex={-1}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-mainText shadow-sm transition ${
                          createViewForm.allow_scroll
                            ? "translate-x-5"
                            : "translate-x-0"
                        }`}
                      />
                    </button>
                  </button>
                </div>

                {/* Footer actions */}
                <div className="flex justify-end gap-2">
                  <button
                    className="rounded-lg border border-btnBorder bg-transparent px-4 py-1.5 text-xs font-normal text-mainText transition hover:bg-btnHover cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2"
                    onClick={resetModal}
                  >
                    Cancel
                  </button>
                  <button
                    className="rounded-lg bg-velBlue px-4 py-1.5 text-xs font-normal text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2"
                    disabled={!createViewForm.name.trim()}
                    onClick={handleModalSubmit}
                  >
                    {editingView ? "Save Changes" : "Create View"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))()}
    </div>
  );
};

export default MainHome;
